import { GeneralCommentsResponse } from "../models/api-response";
import { AxiosRequestConfig } from "axios";
import { callExternalApi, ApiResponse } from "./external-api.service";
import { getLogger } from "src/utils/logger";
import { GeneralCommentsFormData, MathematicsCommentsFormData, EnglishCommentsFormData, ReportNotes } from "src/models/reports";
import { GENERIC_ERROR_MESSAGE } from "src/utils/errorMessages";

const logger = getLogger('reports.service');
const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

export interface ReportComment {
  studentName: string;
  comment: string;
}

export interface ReportsDownloadResponse {
  status: string;
  document: string;  // Base64 encoded Word document
  filename: string;
}

/** The student's name is restored locally, including when it appears in their notes. */
export const generateCommentFromNotes = async (
  student: ReportNotes,
  characterLimit: number,
  accessToken: string | null
): Promise<ApiResponse<ReportComment>> => {
  const name = student.name.trim();
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const anonymise = (text: string) => text.replace(
    new RegExp(`(^|[^\\p{L}\\p{N}])(${escapedName})(?=$|[^\\p{L}\\p{N}])`, 'giu'),
    (_match, prefix: string) => `${prefix}[Student]`
  );
  const response = await callExternalApi({
    config: {
      url: `${apiServerUrl}${accessToken === null ? '/api/public/reports/preview' : '/api/documents/reports'}`,
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(accessToken === null ? {} : { Authorization: `Bearer ${accessToken}` }) },
      data: {
        commentType: 'report',
        formData: {
          characterLimit,
          students: [{
            notes: anonymise(student.notes.trim()),
            pronouns: student.pronouns,
            selectedComments: student.selectedComments.map(anonymise),
            nameLength: Array.from(name).length
          }]
        }
      }
    }
  });
  if (response.error || !response.data || response.data.status !== 'success') {
    return { data: null, error: response.error || { message: response.data?.message || GENERIC_ERROR_MESSAGE } };
  }
  try {
    const content = typeof response.data.content === 'string' ? JSON.parse(response.data.content) : response.data.content;
    const raw: unknown = content?.student_reports?.[0]?.comment;
    if (typeof raw !== 'string' || !raw.trim()) throw new Error('Missing report comment');
    const plainText = raw.replace(/`+\s*(\[Student\](?:['’]s?)?)\s*`+/g, '$1');
    const comment = plainText.replace(/\[Student\]/g, () => name);
    if (Array.from(comment).length > characterLimit) throw new Error('Report exceeds character limit');
    return { data: { studentName: name, comment }, error: null };
  } catch {
    return { data: null, error: { message: 'Could not produce a comment within your character limit. Please try again.' } };
  }
};


/**
 * Sanitizes form data by replacing student names with [Student] placeholder
 * to prevent actual names from being sent to the API
 */
const sanitizeFormData = (formData: any): { sanitizedData: any; nameMap: Map<string, string> } => {
  const nameMap = new Map<string, string>();

  // Start with a shallow copy so we can safely remove root-level identifiers
  const sanitizedData: any = { ...formData };

  // Never send student name identifiers at the root level
  if ("name" in sanitizedData) {
    delete sanitizedData.name;
  }
  if ("studentName" in sanitizedData) {
    delete sanitizedData.studentName;
  }

  // Always expect students array after transformation
  sanitizedData.students = formData.students.map((student: any, index: number) => {
    if (student.name) {
      const studentKey = `student_${index}`;
      nameMap.set(studentKey, student.name);
      return { ...student, name: "[Student]" };
    }
    return student;
  });

  return { sanitizedData, nameMap };
};

/**
 * Restores actual student names in API response by replacing [Student] placeholder
 */
const restoreNamesInResponse = (response: any, nameMap: Map<string, string>): any => {
  if (!response?.studentReports) return response;
  
  const restoredReports = response.studentReports.map((report: any, index: number) => {
    const restoredReport = { ...report };
    const studentKey = `student_${index}`;
    const studentName = nameMap.get(studentKey);
    
    if (studentName) {
      // Restore name in studentName field if present
      if (restoredReport.studentName === '[Student]') {
        restoredReport.studentName = studentName;
      }
      
      // Restore names in comment content for all comment types
      ['generalComment', 'mathsComment', 'englishComment', 'comment'].forEach(commentField => {
        if (restoredReport[commentField]) {
          restoredReport[commentField] = restoredReport[commentField].replace(
            new RegExp('\\[Student\\]', 'g'), 
            studentName
          );
        }
      });
    }
    
    return restoredReport;
  });
  
  return { ...response, studentReports: restoredReports };
};

/**
 * Generate report comments based on the provided form data and comment type
 *
 * @param formData The report form data
 * @param commentType The type of comment to generate (general, math, english)
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to an ApiResponse containing the generated comments or an error
 */
export const generateReportComments = async (
  formData: GeneralCommentsFormData | MathematicsCommentsFormData | EnglishCommentsFormData,
  commentType: 'general' | 'math' | 'english' = 'general',
  accessToken: string
): Promise<ApiResponse<GeneralCommentsResponse>> => {
  // Transform form data to ensure students array format
  let transformedData: any;
  
  if (commentType === 'general') {
    // General comments already have students array
    transformedData = formData;
  } else {
    // Transform single student data to students array format for math/english
    const studentData = { ...formData } as any;
    
    // Map studentName to name for consistency with general comments
    if (studentData.studentName) {
      studentData.name = studentData.studentName;
      delete studentData.studentName;
    }
    
    transformedData = {
      ...formData,
      students: [studentData]
    } as any;

    // Ensure we do not carry any root-level name fields to the API
    if ("studentName" in (transformedData as any)) {
      delete (transformedData as any).studentName;
    }
    if ("name" in (transformedData as any)) {
      delete (transformedData as any).name;
    }
  }
  
  // Sanitize form data to remove student names before API call
  const { sanitizedData, nameMap } = sanitizeFormData(transformedData);
  
  // Map frontend comment types to backend comment types
  const backendCommentType = commentType === 'math' ? 'maths' : commentType;
  
  const config: AxiosRequestConfig = {
    url: `${apiServerUrl}/api/documents/reports`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: { 
      formData: sanitizedData,
      commentType: backendCommentType 
    },
  };
  
  logger.debug(`Generating ${commentType} report comments`);
  const response = await callExternalApi({ config });
  logger.debug(`Response received with status code: ${response.data?.status || 'unknown'}`);
  
  if (response.error || !response.data) {
    logger.error('API call failed:', response.error);
    return {
      data: null,
      error: response.error || { message: `Failed to generate ${commentType} report comments` }
    };
  }
  
  try {
    // Extract content from the backend response
    const { content, document_id } = response.data;
    logger.debug('Raw content from backend:', content);
    logger.debug('Content type:', typeof content);
    
    // Parse content if it's a string, otherwise use as is
    const commentContent = typeof content === 'string' 
      ? JSON.parse(content) 
      : content;
    
    logger.debug('Parsed comment content:', commentContent);
    logger.debug('Student reports from content:', commentContent.student_reports);
    
    // Log safely without risking circular references
    logger.debug(`${commentType} comment content received for ${commentContent.student_reports?.length || 0} students`);
    
    // Restore actual student names in the response
    const responseData = {
      studentReports: commentContent.student_reports || [],
      documentId: document_id
    };
    const restoredData = restoreNamesInResponse(responseData, nameMap);
    
    const result = {
      data: restoredData,
      error: null
    };
    
    logger.debug('Final result being returned:', result);
    
    return result;
  } catch (parseError) {
    logger.error("Failed to parse content:", parseError);
    logger.error(`Content that failed to parse: ${response.data?.content}`);
    return {
      data: null,
      error: { message: GENERIC_ERROR_MESSAGE }
    };
  }
};

/**
 * Generate mathematics comments based on the provided form data
 *
 * @param formData The mathematics form data
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to an ApiResponse containing the generated comments or an error
 */
export const generateMathematicsComments = async (
  formData: MathematicsCommentsFormData,
  accessToken: string
): Promise<ApiResponse<GeneralCommentsResponse>> => {
  return generateReportComments(formData, 'math', accessToken);
};

/**
 * Generate English comments based on the provided form data
 *
 * @param formData The English form data
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to an ApiResponse containing the generated comments or an error
 */
export const generateEnglishComments = async (
  formData: EnglishCommentsFormData,
  accessToken: string
): Promise<ApiResponse<GeneralCommentsResponse>> => {
  return generateReportComments(formData, 'english', accessToken);
};

/**
 * Generate general comments based on the provided form data
 * @deprecated Use generateReportComments with commentType='general' instead
 */
export const generateGeneralComments = async (
  formData: GeneralCommentsFormData,
  accessToken: string
): Promise<ApiResponse<GeneralCommentsResponse>> => {
  return generateReportComments(formData, 'general', accessToken);
};

/**
 * Download report comments as a Word document
 * @param reports Array of report comments with student names
 * @param accessToken Auth0 access token
 * @returns Promise with the download response
 */
export const downloadReportsAsWord = async (
  reports: ReportComment[],
  accessToken: string,
  reportType?: string
): Promise<ApiResponse<ReportsDownloadResponse>> => {
  const config: AxiosRequestConfig = {
    url: `${apiServerUrl}/api/documents/reports/download`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: {
      reports: reports,
      reportType: reportType
    },
  };

  logger.debug(`Downloading ${reports.length} report comments as Word document`);

  // Call the API directly since this endpoint returns a different response structure
  try {
    const response = await callExternalApi({ config });

    if (response.error || !response.data) {
      logger.error('Failed to download reports:', response.error);
      return {
        data: null,
        error: response.error || { message: 'Failed to download report comments' }
      };
    }

    // The download endpoint returns a different structure than other endpoints
    // Cast it as any first to handle the type differences
    const responseData: any = response.data;

    const downloadResponse: ReportsDownloadResponse = {
      status: responseData.status || 'success',
      document: responseData.document || '',
      filename: responseData.filename || `report-comments-${new Date().toISOString().split('T')[0]}.docx`
    };

    return {
      data: downloadResponse,
      error: null
    };
  } catch (error) {
    logger.error('Error downloading reports:', error);
    return {
      data: null,
      error: { message: 'Failed to download report comments' }
    };
  }
};

/**
 * Convert base64 document to blob and trigger download
 * @param base64Document Base64 encoded document
 * @param filename Filename for the download
 */
export const downloadBase64Document = (base64Document: string, filename: string): void => {
  try {
    // Convert base64 to blob
    const byteCharacters = atob(base64Document);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    logger.error('Error downloading document:', error);
    throw error;
  }
};
